import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { ConfirmationDialog } from 'Components/Dialog'
import { SectionLoader } from 'Components/Section'
import { PaginationUI } from 'Components/UI'
import { SchoolPath } from 'Constants/Navigation'
import { URL_KEYS } from 'Constants/Url'
import { EditPencilIcon } from 'Icons/Edit'
import { PlusCircleFilledIcon } from 'Icons/Plus'
import { TrashIcon } from 'Icons/Trash'
import { useStore } from 'effector-react'
import { $Account } from 'Models'
import { UserBasic } from 'Entities/account'
import { MouseEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { isAdminAccount } from 'Utils/AccountRole'
import { useSchoolAnnouncements } from '../Hooks/useSchoolAnnouncements'
import { SchoolAnnouncementDialog } from '../Organisms/SchoolAnnouncementDialog'
import { SchoolAnnouncement, SchoolAnnouncementPayload } from '../types'

const formatDate = (value?: string | null) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

const getExcerpt = (value: string) => {
  const plain = stripHtml(value)
  if (plain.length <= 180) return plain

  return `${plain.slice(0, 180)}...`
}

const getAnnouncementTitle = (item: SchoolAnnouncement) => item.titleRu || item.titleUz || item.titleKo

const getAnnouncementContent = (item: SchoolAnnouncement) => item.contentRu || item.contentUz || item.contentKo

const getSchoolTitle = (item: SchoolAnnouncement) =>
  item.school?.schoolNameRu ||
  item.schoolNameRu ||
  item.school?.schoolNameUz ||
  item.schoolNameUz ||
  item.school?.schoolNameKo ||
  item.schoolNameKo ||
  'Неизвестная школа'

const hasSchool = (item: SchoolAnnouncement) =>
  !!(
    item.school?.schoolId ||
    item.schoolId ||
    item.school?.schoolNameRu ||
    item.schoolNameRu ||
    item.school?.schoolNameUz ||
    item.schoolNameUz ||
    item.school?.schoolNameKo ||
    item.schoolNameKo
  )

const getAccountName = (account: UserBasic | null) => [account?.firstName, account?.lastName].filter(Boolean).join(' ').trim()

const getCreatorName = (item: SchoolAnnouncement, account: UserBasic | null) => {
  const accountName = getAccountName(account)
  if (accountName) return accountName

  const fullName = [item.firstName || item.createdByFirstName, item.lastName || item.createdByLastName].filter(Boolean).join(' ').trim()

  return fullName || item.createdBy || '-'
}

const getAnnouncementOwnerTitle = (item: SchoolAnnouncement, account: UserBasic | null) => {
  if (!hasSchool(item)) return `Superadmin (${getCreatorName(item, account)})`

  return getSchoolTitle(item)
}

const normalizeValue = (value?: string | null) => (value ?? '').trim()

const isAnnouncementPayloadChanged = (item: SchoolAnnouncement, payload: SchoolAnnouncementPayload) => {
  return (
    normalizeValue(item.titleUz) !== payload.titleUz ||
    normalizeValue(item.titleRu) !== payload.titleRu ||
    normalizeValue(item.titleKo) !== payload.titleKo ||
    normalizeValue(item.contentUz) !== payload.contentUz ||
    normalizeValue(item.contentRu) !== payload.contentRu ||
    normalizeValue(item.contentKo) !== payload.contentKo
  )
}

export const SchoolAnnouncementsPage = () => {
  const account = useStore($Account)
  const isAdmin = isAdminAccount(account)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = searchParams.get(URL_KEYS.PAGE) ? Number(searchParams.get(URL_KEYS.PAGE)) : 1
  const search = searchParams.get(URL_KEYS.SEARCH) ?? ''
  const selectedSchoolId = searchParams.get(URL_KEYS.SCHOOL_ID) ? Number(searchParams.get(URL_KEYS.SCHOOL_ID)) : undefined
  const {
    announcements,
    totalCount,
    schoolOptions,
    schoolFilterOptions,
    getAnnouncementId,
    isLoading,
    isFetching,
    createMutation,
    updateMutation,
    deleteMutation,
    uploadAttachmentMutation,
    deleteAttachmentMutation,
  } = useSchoolAnnouncements({
    includeSchools: isAdmin,
    page: page > 0 ? page - 1 : 0,
    search: search.trim() || undefined,
    schoolId: isAdmin ? selectedSchoolId : undefined,
  })
  const schoolTitle = account?.schoolNameRu || account?.schoolNameUz || account?.schoolNameKo || account?.login

  const [searchValue, setSearchValue] = useState(search)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editItem, setEditItem] = useState<SchoolAnnouncement | null>(null)
  const [removeId, setRemoveId] = useState<number | null>(null)

  const attachmentsCount = announcements.reduce((sum, item) => sum + (item.attachments?.length ?? 0), 0)
  const schoolsCount = new Set(announcements.map(item => item.school?.schoolId ?? item.schoolId).filter(Boolean)).size

  useEffect(() => {
    setSearchValue(search)
  }, [search])

  useEffect(() => {
    const normalizedSearchValue = searchValue.trim()
    if (normalizedSearchValue === search) return

    const timeout = window.setTimeout(() => {
      const nextParams = new URLSearchParams(searchParams)
      if (normalizedSearchValue) {
        nextParams.set(URL_KEYS.SEARCH, normalizedSearchValue)
      } else {
        nextParams.delete(URL_KEYS.SEARCH)
      }
      nextParams.set(URL_KEYS.PAGE, '1')
      setSearchParams(nextParams)
    }, 500)

    return () => window.clearTimeout(timeout)
  }, [search, searchParams, searchValue, setSearchParams])

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  const handleSchoolFilterChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams)
    if (value) {
      nextParams.set(URL_KEYS.SCHOOL_ID, value)
    } else {
      nextParams.delete(URL_KEYS.SCHOOL_ID)
    }
    nextParams.set(URL_KEYS.PAGE, '1')
    setSearchParams(nextParams)
  }

  const handleCreate = () => {
    setEditItem(null)
    setDialogOpen(true)
  }

  const handleEdit = (item: SchoolAnnouncement) => {
    setEditItem(item)
    setDialogOpen(true)
  }

  const handleRowClick = (id: number) => {
    navigate(`/${SchoolPath.main}/${SchoolPath.announcements}/${id}`)
  }

  const handleActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
  }

  const uploadFiles = async (announcementId: number, files: File[]) => {
    if (!files.length) return

    await Promise.all(files.map(file => uploadAttachmentMutation.mutateAsync({ announcementId, file })))
  }

  const deleteFiles = async (announcementId: number, attachmentIds: number[]) => {
    if (!attachmentIds.length) return

    await Promise.all(attachmentIds.map(attachmentId => deleteAttachmentMutation.mutateAsync({ announcementId, attachmentId })))
  }

  const syncAttachments = async (announcementId: number, files: File[], removedAttachmentIds: number[]) => {
    await deleteFiles(announcementId, removedAttachmentIds)
    await uploadFiles(announcementId, files)
  }

  const handleSubmit = ({
    payload,
    files,
    removedAttachmentIds,
  }: {
    payload: SchoolAnnouncementPayload
    files: File[]
    removedAttachmentIds: number[]
  }) => {
    if (editItem) {
      const hasPayloadChanges = isAnnouncementPayloadChanged(editItem, payload)
      const hasAttachmentChanges = files.length > 0 || removedAttachmentIds.length > 0

      if (!hasPayloadChanges && !hasAttachmentChanges) {
        setDialogOpen(false)
        setEditItem(null)
        return
      }

      const finishAttachmentSync = async () => {
        try {
          await syncAttachments(editItem.id, files, removedAttachmentIds)
          setDialogOpen(false)
          setEditItem(null)
          toast.success('Объявление обновлено.')
        } catch {
          toast.error('Не удалось обновить вложения объявления.')
        }
      }

      if (!hasPayloadChanges) {
        finishAttachmentSync()
        return
      }

      updateMutation.mutate(
        { id: editItem.id, payload },
        {
          onSuccess: async () => {
            try {
              await syncAttachments(editItem.id, files, removedAttachmentIds)
              setDialogOpen(false)
              setEditItem(null)
              toast.success('Объявление обновлено.')
            } catch {
              toast.error('Объявление сохранено, но не удалось обновить вложения.')
            }
          },
        },
      )
      return
    }

    createMutation.mutate(payload, {
      onSuccess: async data => {
        const announcementId = getAnnouncementId(data)
        if (!announcementId) {
          setDialogOpen(false)
          toast.success('Объявление создано.')
          return
        }

        try {
          await uploadFiles(announcementId, files)
          setDialogOpen(false)
          toast.success('Объявление создано.')
        } catch {
          toast.error('Объявление создано, но не удалось загрузить вложения.')
        }
      },
    })
  }

  const handleRemove = () => {
    if (!removeId) return

    deleteMutation.mutate(removeId, {
      onSuccess: () => {
        setRemoveId(null)
        toast.success('Объявление удалено.')
      },
    })
  }

  const actionLoading =
    createMutation.isLoading || updateMutation.isLoading || uploadAttachmentMutation.isLoading || deleteAttachmentMutation.isLoading

  const renderAnnouncementTable = () => (
    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Table sx={{ minWidth: isAdmin ? 860 : 760 }} aria-label='Announcements table'>
        <TableHead>
          <TableRow
            sx={{
              '& th': {
                bgcolor: 'grey.50',
                color: 'text.secondary',
                fontSize: 13,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              },
            }}>
            <TableCell>Заголовок</TableCell>
            {isAdmin ? <TableCell>Школа</TableCell> : null}
            <TableCell>Вложения</TableCell>
            <TableCell>Опубликовано</TableCell>
            <TableCell>Просмотры</TableCell>
            <TableCell align='right'>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {announcements.map(item => (
            <TableRow
              hover
              key={item.id}
              onClick={() => handleRowClick(item.id)}
              sx={{
                cursor: 'pointer',
                '& td': { py: 1.5, verticalAlign: 'top' },
              }}>
              <TableCell sx={{ minWidth: 280 }}>
                <Typography variant='subtitle2' color='text.primary'>
                  {getAnnouncementTitle(item) || '-'}
                </Typography>
                <Typography variant='body2' color='text.secondary' noWrap sx={{ maxWidth: 420, mt: 0.5 }}>
                  {getExcerpt(getAnnouncementContent(item)) || '-'}
                </Typography>
              </TableCell>
              {isAdmin ? (
                <TableCell sx={{ minWidth: 180 }}>
                  <Typography variant='body2'>{getAnnouncementOwnerTitle(item, account)}</Typography>
                </TableCell>
              ) : null}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.attachments?.length ?? 0}</TableCell>
              <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>{formatDate(item.publishedDate)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.viewCount ?? 0}</TableCell>
              <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                <IconButton
                  size='small'
                  aria-label='Редактировать объявление'
                  onClick={event => {
                    handleActionClick(event)
                    handleEdit(item)
                  }}>
                  <EditPencilIcon />
                </IconButton>
                <IconButton
                  size='small'
                  aria-label='Удалить объявление'
                  onClick={event => {
                    handleActionClick(event)
                    setRemoveId(item.id)
                  }}>
                  <TrashIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <>
      <SchoolAnnouncementDialog
        open={dialogOpen}
        data={editItem}
        isAdmin={isAdmin}
        isLoading={actionLoading}
        schools={schoolOptions}
        onClose={() => {
          setDialogOpen(false)
          setEditItem(null)
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmationDialog open={!!removeId} onAccept={handleRemove} onClose={() => setRemoveId(null)} />

      <Stack spacing={3}>
        <Box>
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
            <Box>
              <Typography variant='h4'>Объявления</Typography>
              <Typography variant='body2' color='text.secondary' mt={0.75}>
                {isAdmin
                  ? 'Просмотр и управление объявлениями всех школ корейского языка.'
                  : schoolTitle
                    ? `${schoolTitle}: создание и управление объявлениями школы.`
                    : 'Создание и управление школьными объявлениями.'}
              </Typography>
            </Box>
            <Button startIcon={<PlusCircleFilledIcon />} onClick={handleCreate}>
              Новое объявление
            </Button>
          </Stack>
        </Box>

        <Grid
          container
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
            },
          }}>
          <Grid>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant='body2' color='text.secondary'>
                Всего объявлений
              </Typography>
              <Typography variant='h4' mt={1}>
                {totalCount}
              </Typography>
            </Paper>
          </Grid>
          <Grid>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant='body2' color='text.secondary'>
                Вложения
              </Typography>
              <Typography variant='h4' mt={1}>
                {attachmentsCount}
              </Typography>
            </Paper>
          </Grid>
          <Grid>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant='body2' color='text.secondary'>
                {isAdmin ? 'Школы' : 'Регион'}
              </Typography>
              <Typography variant='h4' mt={1} noWrap>
                {isAdmin ? schoolsCount : account?.regionName || '-'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={isAdmin ? 8 : 12}>
              <TextField
                fullWidth
                size='small'
                label='Поиск по заголовку'
                value={searchValue}
                onChange={event => handleSearchChange(event.target.value)}
              />
            </Grid>
            {isAdmin ? (
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label='Школа'
                  value={selectedSchoolId || ''}
                  onChange={event => handleSchoolFilterChange(event.target.value)}>
                  <MenuItem value=''>Все школы</MenuItem>
                  {schoolFilterOptions.map(school => (
                    <MenuItem key={school.schoolId} value={school.schoolId}>
                      {school.schoolNameRu || school.schoolNameUz || school.schoolNameKo}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
          </Grid>
        </Paper>

        <SectionLoader isLoading={isLoading} isFetching={isFetching}>
          <Stack spacing={1.5}>
            {announcements.length ? (
              renderAnnouncementTable()
            ) : (
              <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                <Typography variant='h6'>Объявления не найдены</Typography>
                <Typography variant='body2' color='text.secondary' mt={1} mb={2}>
                  Создайте первое многоязычное объявление для этой школы.
                </Typography>
                <Button startIcon={<PlusCircleFilledIcon />} onClick={handleCreate}>
                  Новое объявление
                </Button>
              </Paper>
            )}
          </Stack>
          <PaginationUI length={totalCount} />
        </SectionLoader>
      </Stack>
    </>
  )
}
